import { useCallback, useEffect, useRef, useState } from 'react'
import { StorageService } from '@/services/core/storage'

const PREFIX = 'adnuntius.rui.'

/**
 * The storage key for the signed-in user. Filters are one user's preference —
 * a country picked by an admin must not follow them into a masquerade, where
 * it would silently hide everything the other user can see.
 */
function storageKey(key: string): string {
  const userId = StorageService.getCurrentUser()?.id
  return userId ? `${PREFIX}${userId}.${key}` : PREFIX + key
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(storageKey(key))
    return raw !== null ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

/**
 * `useState` that survives a reload by mirroring the value into localStorage.
 *
 * List filters use this so a reload does not throw the user back to the
 * default view. Keys are namespaced per list (e.g. `filters.orders`) and per
 * user, and are shared across networks — the filters are a view preference,
 * not data.
 */
export function usePersistedState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => read(key, initial))

  // Re-read when the key changes so two lists sharing the hook don't bleed.
  const currentKey = useRef(key)
  useEffect(() => {
    if (currentKey.current !== key) {
      currentKey.current = key
      setValue(read(key, initial))
    }
    // `initial` is only a fallback for a missing entry, so it is not a dependency.
  }, [key, initial])

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(key), JSON.stringify(value))
    } catch {
      // Storage full or blocked — the filter simply won't be remembered.
    }
  }, [key, value])

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(key))
    } catch {
      // ignore
    }
  }, [key])

  return [value, setValue, clear] as const
}
